import React, { useState } from 'react';
import { useMediaQuery } from '@react-hook/media-query';
import Img from 'gatsby-image/withIEPolyfill';
import {
  CarouselProvider,
  Slider,
  Slide,
  ButtonBack,
  ButtonNext,
  DotGroup,
} from 'pure-react-carousel';
import 'pure-react-carousel/dist/react-carousel.es.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { truncateText, convertRToSuperscript } from '../utils/utils';
import { useWindowSize } from '../utils/hooks';
import { GatsbySanityImageFluid, GatsbySanityImageFluidNoBase64 } from '../types';
import GenericLink, { GenericLinkPropsWithTo } from './GenericLink';

import * as styles from './SlideCarousel.module.scss';

type OptionalGenericLinkProps = Omit<GenericLinkPropsWithTo, 'to'> & {
  to?: string;
  children: React.ReactNode;
};

const OptionalGenericLink = ({
  to,
  children,
  ...restProps
}: OptionalGenericLinkProps): React.ReactElement => {
  return to ? (
    <GenericLink className={styles.imageLink} to={to} {...restProps}>
      {children}
    </GenericLink>
  ) : (
    <>{children}</>
  );
};

interface CarouselProps {
  slides: Array<{
    imageFluid: GatsbySanityImageFluid | GatsbySanityImageFluidNoBase64;
    text: string;
    linkUrl?: string;
  }>;
}

const SlideCarousel = ({ slides }: CarouselProps): React.ReactElement => {
  const { width } = useWindowSize();
  const [wasInteractedWith, setWasInteractedWith] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const isMobile = useMediaQuery('(max-width: 519px)');

  return (
    <CarouselProvider
      className={styles.carousel}
      infinite
      naturalSlideWidth={!width || width >= 1200 ? 3 : 2.5}
      naturalSlideHeight={1}
      totalSlides={slides.length}
      isPlaying={!isMobile && !wasInteractedWith && !isHovered}
      interval={4000}
    >
      <Slider className={styles.slider}>
        {slides.map((slide, i) => (
          <Slide
            key={i}
            className={styles.slide}
            index={i}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <OptionalGenericLink noUnderline noExternalUrlIcon to={slide.linkUrl}>
              <Img
                className={styles.image}
                loading={i === 0 ? 'eager' : undefined}
                fadeIn={i !== 0}
                fluid={slide.imageFluid}
                alt={slide.text}
              />
            </OptionalGenericLink>
            <div
              className={
                'dark ' + styles.bottomBar + (slides.length === 1 ? ' ' + styles.noDotGroup : '')
              }
            >
              <h2 className={styles.text}>{convertRToSuperscript(truncateText(slide.text, 80))}</h2>
            </div>
          </Slide>
        ))}
      </Slider>
      {slides.length > 1 && (
        <>
          <ButtonBack
            className={styles.button + ' ' + styles.buttonBack}
            onClick={() => {
              setWasInteractedWith(true);
            }}
          >
            <FontAwesomeIcon icon="chevron-left" className={styles.buttonIcon} />
          </ButtonBack>
          <ButtonNext
            className={styles.button + ' ' + styles.buttonNext}
            onClick={() => {
              setWasInteractedWith(true);
            }}
          >
            <FontAwesomeIcon icon="chevron-right" className={styles.buttonIcon} />
          </ButtonNext>
          <DotGroup
            className={styles.dotGroup}
            onClick={() => {
              setWasInteractedWith(true);
            }}
          ></DotGroup>
        </>
      )}
    </CarouselProvider>
  );
};

export default SlideCarousel;
